import React, { useState, useEffect, useCallback, useMemo } from "react";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import UserImg from "@styles/all-images/images/Avatar.png";
import ChatMessages from "../components/ChatMessages";
import { useLiveChatState } from "../Provider/LiveChatStateProvider";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { ChatScreenType, ChatUser, UserStandaloneInfo } from "../types/chat";
import closeIcon from "@styles/all-images/icons/close.svg";
import {
  DocumentApprovalStatus,
  DocumentApprovalStatusText,
} from "@/constants/enums/document-approval-status.enum";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { StandaloneChatDrawerType } from ".";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import { S3Image } from "@/utils/s3-helper";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";

export interface DocumentInfo {
  id: number;
  title: string;
  type?: string;
  status: DocumentApprovalStatus;
}

interface ChatComponentProps {
  onClose?: () => void;
  isChatNotificationOpen: boolean;
  setIsChatNotificationOpen: (value: boolean) => void;
  documentId?: number;
  userId: number;
  documentInfo?: DocumentInfo;
  customChatRoomId: string | null;
  type: StandaloneChatDrawerType;
}

const ChatComponent: React.FC<ChatComponentProps> = ({
  onClose,
  isChatNotificationOpen,
  setIsChatNotificationOpen,
  documentId,
  userId,
  documentInfo,
  customChatRoomId,
  type,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [userStandaloneInfo, setUserStandaloneInfo] =
    useState<UserStandaloneInfo | null>(null);
  const [userInfo, setUserInfo] = useState<ChatUser | null>(null);
  const [threadMessageId, setThreadMessageId] = useState<number | undefined>(
    undefined,
  );
  const [customRoomId, setCustomRoomId] = useState<string | null>(
    customChatRoomId,
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const handleCloseChatNotification = () => {
    setIsChatNotificationOpen(false);
    onClose?.();
  };

  const handleSettingDocumentChatRoomId = useCallback(async () => {
    if (customChatRoomId) {
      setCustomRoomId(customChatRoomId);
    } else {
      try {
        const { authenticatedCall } = actionsHandler("createDocumentChatRoom");
        const response = await dispatch(
          authenticatedCall({
            method: "post",
            payload: {
              document_id: documentId,
              recipient_id: userId,
            },
          }),
        );
        if (response.ok) {
          setCustomRoomId(response.data?.room_id?.toString() || null);
        }
      } catch (error) {
        console.error("Error creating chat room:", error);
      }
    }
  }, [documentId, userId]);

  const getUserInfo = useCallback(async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const { authenticatedCall } = actionsHandler("getUserInfo");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [userId + "/"],
          }),
        );

        if (response.ok) {
          resolve(response.data);
        } else {
          reject("Failed to get user info");
        }
      } catch (error) {
        reject(error);
      }
    });
  }, [userId]);

  const handleSettingChatStateData = useCallback(async () => {
    try {
      const userInfo = await getUserInfo();
      setUserStandaloneInfo(userInfo as UserStandaloneInfo);
    } catch (error) {
      console.error(error);
    }
  }, [getUserInfo]);

  const isRoomIdValid = useMemo(() => {
    return !!userInfo?.room_id || !!customRoomId;
  }, [customRoomId, userInfo]);

  useEffect(() => {
    const userInfo = {
      ...userStandaloneInfo,
      room_id: userStandaloneInfo?.room_id?.toString?.() || "",
    };
    setUserInfo(userInfo as ChatUser);
  }, [userStandaloneInfo]);

  const isUserDetailAvailable = useMemo(() => {
    return userInfo?.id;
  }, [user, userInfo]);

  useEffect(() => {
    if (
      type === StandaloneChatDrawerType.USER ||
      user?.user_type?.id === UserTierType.Tier3
    ) {
      handleSettingChatStateData();
    }
  }, [handleSettingChatStateData, type, user]);

  useEffect(() => {
    if (type === StandaloneChatDrawerType.DOCUMENT) {
      handleSettingDocumentChatRoomId();
    }
  }, [handleSettingDocumentChatRoomId, type]);

  useEffect(() => {
    if (customChatRoomId) {
      setCustomRoomId(customChatRoomId);
    }
  }, [customChatRoomId]);

  return (
    <div
      className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-full tw-bg-black/25 tw-shadow-lg tw-transition-transform tw-duration-300 tw-ease-in-out 
        ${
          isChatNotificationOpen
            ? "tw-transform tw-translate-x-0"
            : "tw-transform tw-translate-x-full"
        } tw-z-50`}
    >
      <div className="tw-w-[477px] tw-fixed tw-right-0 tw-top-0 tw-bottom-0 tw-bg-black/25">
        <div className="tw-bg-white tw-h-full tw-w-full tw-flex tw-flex-col">
          {/* Fixed Header */}
          {isUserDetailAvailable && (
            <div className="tw-flex tw-justify-between tw-items-start tw-p-2 tw-m-2 tw-border-b tw-border-[#EAECF0] tw-flex-shrink-0">
              <div className="tw-flex tw-items-center tw-space-x-3">
                <div className="tw-relative">
                  {userInfo?.profile_picture ? (
                    <S3Image
                      src={
                        userInfo?.profile_picture?.replace(
                          "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
                          "",
                        ) || null
                      }
                      alt="Profile"
                      cacheKey={userInfo?.profile_picture}
                      className="tw-w-10 tw-h-10 tw-rounded-full"
                    />
                  ) : (
                    <img
                      src={UserDropdown}
                      alt="Profile"
                      className="tw-w-10 tw-h-10 tw-rounded-full"
                    />
                  )}
                  <span
                    className={`tw-absolute -tw-top-[2px] -tw-right-[4px] tw-h-[6px] tw-w-[6px] tw-p-[6px] tw-rounded-full tw-bg-[#717680] tw-border-2 tw-border-[#FFFFFF] ${
                      userStandaloneInfo?.is_online
                        ? "tw-bg-[#12B76A]"
                        : "tw-bg-[#717680]"
                    }`}
                  ></span>
                </div>
                <div>
                  <h3 className="font-primary-color tw-text-base tw-font-semibold">
                    {userStandaloneInfo?.name}
                  </h3>
                  <p className="tw-text-sm tw-text-[#475569] tw-font-normal">
                    {userStandaloneInfo?.default_profile?.name}
                  </p>
                </div>
              </div>
              <button onClick={handleCloseChatNotification} className="tw-p-2">
                <img src={CloseIcon} alt="Close" className="tw-w-4 tw-h-4" />
              </button>
            </div>
          )}
          {documentInfo && (
            <div className="tw-border-b tw-border-[#EAECF0] tw-flex tw-items-center tw-justify-between tw-p-4 tw-flex-shrink-0">
              <span className="font-primary-color font-size-sm tw-font-medium">
                {documentInfo.title}
              </span>
              <span className="font-gray-color font-size-sm tw-font-medium">
                <StatusChips
                  status={documentInfo.status}
                  type={StatusChipEnumType.DocumentApproval}
                />
              </span>
              {!isUserDetailAvailable && (
                <button
                  onClick={handleCloseChatNotification}
                  className="tw-p-2"
                >
                  <img src={CloseIcon} alt="Close" className="tw-w-4 tw-h-4" />
                </button>
              )}
            </div>
          )}

          {isChatNotificationOpen && isRoomIdValid && (
            <>
              {Boolean(threadMessageId) && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-py-4 tw-px-4 tw-rounded-t-lg tw-bg-[#FFFFFF] tw-border-b tw-border-[#E9EAEB]">
                  <button
                    onClick={() => setThreadMessageId(undefined)}
                    className="tw-text-gray-400 tw-hover:text-gray-600"
                  >
                    <img
                      src={BackIcon}
                      alt="Back Icon"
                      className="tw-w-4 tw-h-4"
                    />
                  </button>
                  <h6 className="tw-text-[#181D27] tw-text-sm tw-font-medium">
                    Thread
                  </h6>
                </div>
              )}
              <ChatMessages
                isStandalone={true}
                standaloneUserInfo={userInfo || undefined}
                customRoomId={customRoomId || undefined}
                documentId={documentId}
                threadMessageId={threadMessageId}
                isThread={Boolean(threadMessageId)}
                onReplyClick={(message) => {
                  setThreadMessageId(message.id);
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;
